/**
 * Authentication Context
 * 
 * Provides authentication state and functions to the entire application.
 * Handles login, logout, session management, and user state.
 * Implements sliding sessions - tokens are auto-refreshed while user is active.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/authHelpers';
import { checkSessionExpiredOnLoad } from '../hooks/useInactivityTimeout';
import { setupTokenRefreshInterceptor } from '../hooks/useTokenRefresh';
import {
  startAuthBackgroundService,
  stopAuthBackgroundService,
} from '../utils/authBackgroundService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create the context
const AuthContext = React.createContext();

/**
 * AuthProvider Component
 * Wraps the application and provides authentication state
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true); // The "Lock" - prevents rendering until auth state is known

  /**
   * Logout - clear all auth state
   *
   * (Feb 2026 Workstream A): stops the module-level background service
   * instead of clearing a React-owned interval ref.
   */
  const logout = useCallback(() => {
    // Server-side revocation: denylist the token so it stops working
    // everywhere immediately (fire-and-forget — logout must never block).
    const _tok = getAuthToken();
    if (_tok) {
      axios.post(`${API}/auth/logout`, null, {
        headers: { Authorization: `Bearer ${_tok}` },
      }).catch(() => { /* already clearing locally */ });
    }
    clearAuthToken();
    sessionStorage.removeItem('impersonatedUserId');
    sessionStorage.removeItem('lastActivityTimestamp');
    sessionStorage.removeItem('activeClientId'); // Clear stored client chart
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Impersonate-User'];

    // Stop module-level background service (token refresh + activity listeners).
    stopAuthBackgroundService();

    // Clear stale requests
    import('../utils/requestDeduplication').then(({ clearPendingRequests }) => {
      clearPendingRequests();
    });
    import('../utils/staticDataCache').then(({ clearStaticDataCache }) => {
      clearStaticDataCache();
    });

    setUser(null);
  }, []);

  /**
   * Fetch current user from backend to verify token
   *
   * (Feb 2026 Phase-1 stabilization): wrapped in ``useCallback`` with an
   * empty deps array so its identity is stable across renders.  Without
   * useCallback, every render of ``AuthProvider`` produced a brand-new
   * function reference, which was then placed into the Provider ``value``
   * object — defeating the ``useMemo`` we add below.  The function reads
   * no provider-level vars (only the stable setters from useState and the
   * module-level ``API`` const), so an empty deps array is correct.
   */
  const fetchCurrentUser = useCallback(async () => {
    try {
      void 0 && console.log('[AUTH] Fetching current user from /auth/me...');
      const response = await axios.get(`${API}/auth/me`);
      void 0 && console.log('[AUTH] User fetched successfully:', response.data?.email);
      setUser(response.data);
    } catch (error) {
      console.error('[AUTH] Failed to fetch current user:', error.message);
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Token definitively invalid/expired - clean up
        clearAuthToken();
        sessionStorage.removeItem('impersonatedUserId');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } else {
        // Transient failure (network/5xx) — keep whatever session state we
        // have; do NOT wipe the token of a possibly-valid session.
        setUser((prev) => prev || null);
      }
    } finally {
      setIsInitialLoading(false); // Open the door - auth state is now known
    }
  }, []);

  /**
   * (Feb 2026 Workstream A) — the background service start/stop is now
   * driven by a simple useEffect below.  The previous
   * ``setupTokenRefresh`` useCallback + its setInterval + its 4
   * window event listeners have all moved to
   * /utils/authBackgroundService.js — they live at module scope,
   * outside React's render tree.  They are now attached ONCE per page
   * load (instead of being re-registered on every AuthProvider
   * render) and updating ``lastActivity`` no longer triggers any
   * React state change.
   */

  // Initial auth check on mount
  useEffect(() => {
    // Setup token refresh interceptor for handling 401 errors
    setupTokenRefreshInterceptor(logout);

    // Clear any stale impersonation header
    delete axios.defaults.headers.common['X-Impersonate-User'];
    
    // Check if session expired due to inactivity
    const sessionExpired = checkSessionExpiredOnLoad();
    if (sessionExpired) {
      void 0 && console.log('[AUTH] Session expired due to inactivity');
      sessionStorage.removeItem('impersonatedUserId');
      setIsInitialLoading(false);
      return;
    }
    
    const token = getAuthToken();
    void 0 && console.log('[AUTH] Initial load - Token:', token ? 'exists' : 'none');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      void 0 && console.log('[AUTH] No token found');
      sessionStorage.removeItem('impersonatedUserId');
      setIsInitialLoading(false); // No token, nothing to fetch
    }
  }, [logout]);

  // Setup token refresh when user is logged in.
  // (Feb 2026 Workstream A): start/stop a module-level singleton service
  // instead of inline setInterval + 4 event-listener attachments.
  useEffect(() => {
    if (user) {
      startAuthBackgroundService({ onTokenRefreshFail: logout });
      return () => stopAuthBackgroundService();
    }
  }, [user, logout]);

  /**
   * Login with email and password
   *
   * (Feb 2026 Phase-1): wrapped in ``useCallback`` for identity stability,
   * so consumers that destructure ``login`` from useAuth() (e.g. the
   * LoginRoute) don't see a fresh function reference on every Auth
   * provider render and re-fire any effect that depends on it.
   */
  const login = useCallback(async (email, password) => {
    // Clear any previous session
    clearAuthToken();
    sessionStorage.removeItem('impersonatedUserId');
    delete axios.defaults.headers.common['X-Impersonate-User'];
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    
    // Clear stale requests
    import('../utils/requestDeduplication').then(({ clearPendingRequests }) => {
      clearPendingRequests();
    });
    import('../utils/staticDataCache').then(({ clearStaticDataCache }) => {
      clearStaticDataCache();
    });
    
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    setAuthToken(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    
    return userData;
  }, []);

  /**
   * Login with an existing token (used after 2FA verification, password change, etc.)
   * Verifies the token with the backend before setting user state
   *
   * (Feb 2026 Phase-1): wrapped in ``useCallback`` — depends on
   * ``logout`` which is itself useCallback'd with [], so this stays
   * stable across renders.
   */
  const loginWithToken = useCallback(async (token) => {
    void 0 && console.log('[AUTH] loginWithToken called');
    
    // Clear any previous session
    clearAuthToken();
    sessionStorage.removeItem('impersonatedUserId');
    sessionStorage.removeItem('activeClientId'); // Clear any stored client chart to ensure dashboard shows
    delete axios.defaults.headers.common['X-Impersonate-User'];
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear stale requests
    import('../utils/requestDeduplication').then(({ clearPendingRequests }) => {
      clearPendingRequests();
    });
    import('../utils/staticDataCache').then(({ clearStaticDataCache }) => {
      clearStaticDataCache();
    });
    
    // Set the new token
    setAuthToken(token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    try {
      // Verify token by fetching user from backend
      void 0 && console.log('[AUTH] Verifying token with /auth/me...');
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      void 0 && console.log('[AUTH] loginWithToken complete for:', response.data?.email);
      return true;
    } catch (error) {
      console.error('[AUTH] loginWithToken failed:', error.message);
      logout(); // Clean up if token was bad
      return false;
    }
  }, [logout]);

  /**
   * Create new user (admin only)
   *
   * (Feb 2026 Phase-1): wrapped in ``useCallback`` for identity stability.
   */
  const createUser = useCallback(async (email, password, full_name, role) => {
    const response = await axios.post(`${API}/auth/create-user`, {
      email,
      password,
      full_name,
      role
    });
    return response.data;
  }, []);

  // Phase-1 stabilization: memoize the Provider value so consumers
  // don't re-render on every AuthProvider render — only when one of
  // the listed fields actually changes.  Setters from ``useState`` are
  // stable across renders so they are intentionally NOT in the deps
  // array.  Functions are now useCallback-stable above.
  const value = useMemo(() => ({
    user,
    login,
    loginWithToken,
    createUser,
    logout,
    loading: isInitialLoading, // Keep 'loading' name for backward compatibility
    refreshUser: fetchCurrentUser,
    setUser,
  }), [
    user,
    isInitialLoading,
    // Stable function refs — included for correctness; they only change
    // when their own deps change (which is also rare).
    login,
    loginWithToken,
    createUser,
    logout,
    fetchCurrentUser,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 * Provides access to authentication context
 */
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
